import { yupResolver } from '@hookform/resolvers/yup';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	Stack,
	Typography,
} from '@mui/material';
import React from 'react';
import { SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { SelectElement } from 'react-hook-form-mui';
import * as yup from 'yup';
import { useConfigContext } from '../../../../../../context/config';
import {
	AutofillCallResponse,
	AutofillIntegrationCreateRequest,
	ExternalServiceIntegration,
} from '../../../api/integrations';
import {
	ACTION_CANCEL,
	ACTION_CREATE,
	ACTION_SAVE,
	ADD_LABEL,
	AUTOFILL_LABEL,
	AUTOFILL_PLUGIN_LABEL,
	AUTOFILL_SEQUENCE_LABEL,
	AUTOFILL_SEQUENCE_OPERATION_LABEL,
	AUTOFILL_SEQUENCE_OPERATION_SEQUENCE_LABEL,
	AUTOFILL_SEQUENCE_OPERATION_TYPE_LABEL,
	AUTOFILL_SEQUENCE_PRODUCT_LABEL,
	DATA_AUTOFILL_LABEL,
	EDIT_LABEL,
	NEW_LABEL,
	WEBHOOK_CONFIGURATION_MODAL_REQUIRED_MESSAGE,
} from '../../../data/constants';
import { handleModalClose } from '../../../utils/handleModalClose';

interface DataAutofillIntegration {
	name: string;
}

interface IFormInputs {
	product: string;
	operation: string;
	operationType: string;
	operationSequence: DataAutofillIntegration[];
}

const AUTOFILL_SEQUENCE_FORM_KEYS = {
	PRODUCT: 'product',
	OPERATION: 'operation',
	OPERATION_TYPE: 'operationType',
	OPERATION_SEQUENCE: 'operationSequence',
};

interface Props {
	autofillSequence: AutofillCallResponse;
	autofillIntegrations: ExternalServiceIntegration[];
	open: boolean;
	onCancel: () => void;
	onAddUpdate: (
		autofillSequenceAddReq: AutofillIntegrationCreateRequest,
	) => void;
	isLoading: boolean;
}

export default function AddUpdateAutofillSequenceModal(props: Props) {
	const {
		open,
		autofillIntegrations,
		autofillSequence,
		onAddUpdate,
		onCancel,
		isLoading,
	} = props;
	const config = useConfigContext();
	const productConfigs = config?.productConfigs;
	const isEditing = !!autofillSequence;

	const productOptions = productConfigs
		? productConfigs
				.filter((config) => config.name !== 'global')
				.map((product) => {
					return {
						id: product.name,
						label: product.name,
					};
				})
		: [];
	productOptions.push({ id: '*', label: 'Any' });
	const operationOptions = [
		{ id: 'newBusiness', label: 'New Business' },
		{ id: 'endorsement', label: 'Endorsement' },
		{ id: 'renewal', label: 'Renewal' },
		{ id: '*', label: 'Any' },
	];
	const operationTypeOptions = [
		{ id: 'create', label: 'Create' },
		{ id: 'update', label: 'Update' },
		{ id: '*', label: 'Any' },
	];
	const autofillOptions = autofillIntegrations?.map((autofill) => {
		return {
			id: autofill.name,
			label: autofill.name, // use "label" for AutocompleteElement
		};
	});
	autofillOptions.push({ id: 'internal', label: AUTOFILL_PLUGIN_LABEL() });

	const resolver = yupResolver(
		yup.object().shape({
			[AUTOFILL_SEQUENCE_FORM_KEYS.PRODUCT]: yup
				.string()
				.required(WEBHOOK_CONFIGURATION_MODAL_REQUIRED_MESSAGE()),
			[AUTOFILL_SEQUENCE_FORM_KEYS.OPERATION]: yup
				.string()
				.required(WEBHOOK_CONFIGURATION_MODAL_REQUIRED_MESSAGE()),
			[AUTOFILL_SEQUENCE_FORM_KEYS.OPERATION_TYPE]: yup
				.string()
				.required(WEBHOOK_CONFIGURATION_MODAL_REQUIRED_MESSAGE()),
			[AUTOFILL_SEQUENCE_FORM_KEYS.OPERATION_SEQUENCE]: yup.lazy(() =>
				yup.array().of(
					yup.object({
						name: yup
							.string()
							.required(WEBHOOK_CONFIGURATION_MODAL_REQUIRED_MESSAGE()),
					}),
				),
			),
		}),
	);

	const { handleSubmit, control } = useForm<IFormInputs>({
		defaultValues: {
			product: autofillSequence?.operation.split('.')[0] || '',
			operation: autofillSequence?.operation.split('.')[1] || '',
			operationType: autofillSequence?.operation.split('.')[2] || '',
			operationSequence: autofillSequence?.callSequence?.map((autofill) => {
				return { name: autofill };
			}) || [{ name: '' }],
		},
		mode: 'onBlur',
		shouldFocusError: true,
		resolver,
	});

	const { fields, remove, append, swap } = useFieldArray({
		control,
		name: 'operationSequence',
	});

	const onSubmit: SubmitHandler<IFormInputs> = async (data) => {
		const { product, operation, operationType, operationSequence } = data;
		const request = {} as AutofillIntegrationCreateRequest;

		if (product && operation && operationType) {
			request.operation = `${product}.${operation}.${operationType}`;
		}
		if (operationSequence && operationSequence.length > 0) {
			request.callSequence = operationSequence.map((autofill) => autofill.name);
		}
		if (!isLoading) {
			await onAddUpdate(request);
		}
	};

	const sequenceArrows = (index: number) => {
		const displayChevronUp = index > 0 && fields.length > 1;
		const displayChevronDown = index < fields.length - 1 && fields.length > 1;

		return (
			<Stack direction='column'>
				<KeyboardArrowUpIcon
					sx={{
						width: '0.875em',
						height: '0.875em',
						cursor: displayChevronUp ? 'pointer' : 'default',
						color: displayChevronUp ? 'primary.lighter' : 'black.light',
					}}
					onClick={() => {
						if (displayChevronUp) {
							swap(index, index - 1);
						}
					}}
				/>
				<KeyboardArrowDownIcon
					sx={{
						width: '0.875em',
						height: '0.875em',
						cursor: displayChevronDown ? 'pointer' : 'default',
						color: displayChevronDown ? 'primary.lighter' : 'black.light',
					}}
					onClick={() => {
						if (displayChevronDown) {
							swap(index, index + 1);
						}
					}}
				/>
			</Stack>
		);
	};

	return (
		<Dialog
			fullWidth
			open={open}
			aria-labelledby='add-update-autofill-sequence-modal'
			onClose={(e, r) => handleModalClose(r, onCancel)}
		>
			<DialogTitle>
				{!isEditing
					? NEW_LABEL(AUTOFILL_SEQUENCE_LABEL)
					: EDIT_LABEL(AUTOFILL_SEQUENCE_LABEL)}
			</DialogTitle>
			<DialogContent>
				<Box component='form' onSubmit={handleSubmit(onSubmit)}>
					<Stack alignItems='flex-start' direction='column' spacing={2} mt={1}>
						<SelectElement
							name='product'
							label={AUTOFILL_SEQUENCE_PRODUCT_LABEL()}
							options={productOptions}
							control={control}
							fullWidth
							disabled={isEditing}
							variant='outlined'
						/>
						<SelectElement
							name='operation'
							label={AUTOFILL_SEQUENCE_OPERATION_LABEL()}
							options={operationOptions}
							control={control}
							fullWidth
							disabled={isEditing}
							variant='outlined'
						/>
						<SelectElement
							name='operationType'
							label={AUTOFILL_SEQUENCE_OPERATION_TYPE_LABEL()}
							options={operationTypeOptions}
							control={control}
							fullWidth
							disabled={isEditing}
							variant='outlined'
						/>
						<Typography>
							{AUTOFILL_SEQUENCE_OPERATION_SEQUENCE_LABEL()}
						</Typography>
						{fields.map((field, index) => (
							<Box
								key={field.id}
								sx={{
									display: 'flex',
									alignItems: 'center',
									flexDirection: 'row',
									width: '100%',
								}}
							>
								<Typography variant='body1' color='body2' sx={{ mr: 1 }}>
									{index + 1}
								</Typography>
								<SelectElement
									name={`operationSequence.${index}.name`}
									label={DATA_AUTOFILL_LABEL()}
									options={autofillOptions}
									variant='outlined'
									size='small'
									fullWidth
									control={control}
								/>
								<Box sx={{ flex: 1, ml: 1, mr: 0.5 }}>
									{sequenceArrows(index)}
								</Box>

								{fields.length > 1 && (
									<Box sx={{ flex: 1 }}>
										<IconButton
											onClick={() => {
												remove(index);
											}}
										>
											<CloseIcon />
										</IconButton>
									</Box>
								)}
							</Box>
						))}
						<Button
							startIcon={<AddIcon />}
							variant='text'
							onClick={() => {
								append({ name: '' });
							}}
						>
							{ADD_LABEL(AUTOFILL_LABEL)}
						</Button>
					</Stack>
					<Stack
						direction='row'
						spacing={2}
						justifyContent='flex-end'
						sx={{ mt: 2 }}
					>
						<Button onClick={onCancel} color='neutralButton'>
							{ACTION_CANCEL()}
						</Button>
						<Button type='submit' variant='contained' color='secondary'>
							{!isLoading ? (
								!isEditing ? (
									ACTION_CREATE()
								) : (
									ACTION_SAVE()
								)
							) : (
								<CircularProgress size={20} style={{ color: 'white' }} />
							)}
						</Button>
					</Stack>
				</Box>
			</DialogContent>
		</Dialog>
	);
}
